import React from 'react'
import { Button } from 'syft-acp-atoms/Button'
import { format } from 'date-fns'
import { ConfirmStepProps } from '../BulkShiftRateUpdateModal.types'

export const ConfirmStep: React.FC<ConfirmStepProps> = ({
  rateRanges,
  rateUpdates,
  onBack,
  onConfirm,
  onCancel,
}) => {
  const formatCurrency = (amount: number | string, currency: string) => {
    const symbol = currency === 'GBP' ? '£' : '$'
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return `${symbol}${numAmount.toFixed(2)}`
  }

  const formatDateRange = (startDate: string, endDate: string | null) => {
    const start = format(new Date(startDate), 'MMM d, yyyy')
    const end = endDate ? format(new Date(endDate), 'MMM d, yyyy') : 'Ongoing'
    return `${start} - ${end}`
  }

  // Filter to only show ranges where the rate is actually changing
  const changedRanges = rateRanges.filter(range => {
    const rangeKey = `${range.start_date}_${range.end_date}`
    const newRate = parseFloat(rateUpdates[rangeKey])
    const currentRate =
      typeof range.client_rate === 'string' ? parseFloat(range.client_rate) : range.client_rate
    return newRate !== currentRate
  })

  // Calculate total shifts affected by actual changes
  const changedShiftCount = changedRanges.reduce((sum, range) => sum + range.shift_count, 0)

  // Handle case where no rates are actually changing
  if (changedRanges.length === 0) {
    return (
      <div>
        <h2 style={{ marginBottom: '1rem' }}>No Changes Detected</h2>

        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f0f8ff',
            border: '1px solid #4a90e2',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          <strong>ℹ️ Notice:</strong> All rates match the current rates for these shifts. No updates will be
          made.
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button kind="regular" onClick={onBack}>
            Back to Rate Input
          </Button>
          <Button kind="neutral" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>Confirm Bulk Rate Update</h2>

      <div
        style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          marginBottom: '1rem',
        }}
      >
        <strong>⚠️ Warning:</strong> You are about to update rates for <strong>{changedShiftCount}</strong>{' '}
        shifts across <strong>{changedRanges.length}</strong> time{' '}
        {changedRanges.length === 1 ? 'period' : 'periods'}.
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                Date Range
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #ddd' }}>
                Shifts
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                Current Rate
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #ddd' }}>
                New Rate
              </th>
            </tr>
          </thead>
          <tbody>
            {changedRanges.map(range => {
              const rangeKey = `${range.start_date}_${range.end_date}`
              const newRate = parseFloat(rateUpdates[rangeKey])
              const currentRate =
                typeof range.client_rate === 'string' ? parseFloat(range.client_rate) : range.client_rate

              return (
                <tr key={rangeKey} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.75rem' }}>{formatDateRange(range.start_date, range.end_date)}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>{range.shift_count}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', color: '#999' }}>
                    {formatCurrency(currentRate, range.currency)}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(newRate, range.currency)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          padding: '1rem',
          backgroundColor: '#f0f8ff',
          border: '1px solid #4a90e2',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.875rem',
        }}
      >
        <strong>This will:</strong>
        <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
          <li>Update client_raw_rate for all matching shifts</li>
          <li>Create audit records for each shift</li>
          <li>Update worker rates if the new rate is higher (cascade)</li>
        </ul>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button kind="regular" onClick={onBack}>
          Back
        </Button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button kind="neutral" onClick={onCancel}>
            Cancel
          </Button>
          <Button kind="success" onClick={onConfirm}>
            Update {changedShiftCount} {changedShiftCount === 1 ? 'Shift' : 'Shifts'}
          </Button>
        </div>
      </div>
    </div>
  )
}
